import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { patchCustomModel, getCustomModels } from 'entities/CustomModel/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useCustomModelStore } from 'widgets/customModel/state';
import ModelSettings from 'components/model-settings';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

const Settings: FC = () => {
	const { modelId } = useParams();
	const { t } = useTranslation();
	const { customModels, setCustomModels } = useCustomModelStore();

	const currentCustomModel = useMemo(
		() => customModels?.find((item) => item._id === modelId),
		[customModels, modelId],
	);

	const { refetch } = useQuery({
		queryKey: ['get/custom-models'],
		queryFn: () => getCustomModels(),
		onSuccess: (response) => {
			setCustomModels(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate, isLoading } = useMutation({
		mutationKey: ['post/files/settings'],
		mutationFn: patchCustomModel,
		onSuccess: async () => {
			await refetch();
			enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	if (!currentCustomModel || !modelId) return null;

	return (
		<PageWrapper>
			<ModelSettings
				isLoading={isLoading}
				modelSettings={{
					name: currentCustomModel.name,
					settings: currentCustomModel?.settings,
				}}
				onSubmit={({ id, data }) => mutate({ customModelId: id, data })}
				modelId={modelId}
				description={t('custom_model.settings.description')}
			/>
		</PageWrapper>
	);
};

export default Settings;
